import {
    cloneDeep,
    entries,
    flow,
    head,
    isNil,
    last,
    maxBy,
    minBy,
    partialRight,
    uniqBy,
} from "lodash";
import AggrTypes from "../queries/technogroup-result-details/AggrTypes";
import {isFiniteNumber} from "./MathUtils";
import {evalInScope} from "./Utils";

const _sumByFieldGroup = (array, field, valueField) =>
    array.reduce((acc, item) => {
        const key = item[field];
        const value = valueField ? item[valueField] || 0 : 1;
        acc[key] = (acc[key] || 0) + value;
        return acc;
    }, {});

function getMostCommon(array, field, countKey) {
    return flow(
        (a, f) => _sumByFieldGroup(a, f, countKey),
        entries,
        partialRight(maxBy, last),
        head
    )(array, field);
}

function getCountDistinct(array, field) {
    const res = uniqBy(array, field);
    return res.filter((item) => !!item[field]).length;
}

export function createDetail(
    aggregate,
    aggregateOptions,
    field,
    pageData,
    filteredData,
    {cache = {}, cacheKey, formatter, onlyGlobal, returnAsNumber}
) {
    let tmpCacheKey = cacheKey || field;
    let asNumber = !!returnAsNumber;

    const formatValue = (val) => {
        if (formatter) {
            const formatted = formatter(val);
            return asNumber ? +formatted : formatted;
        }
        return asNumber ? +val : val;
    };

    try {
        switch (aggregate) {
            case AggrTypes.NONE: {
                return null;
            }
            case AggrTypes.WEIGHTED_AVERAGE: {
                const startingValue = {mass: 0, count: 0};
                const handler = (acc, cur) => {
                    if (!isNil(cur[field])) {
                        const mass = evalInScope(
                            `${aggregateOptions?.massKey || field} * ${aggregateOptions?.countKey}`,
                            cur
                        );
                        if (isFiniteNumber(mass)) {
                            acc.mass = acc.mass + mass;
                            acc.count +=
                                evalInScope(
                                    aggregateOptions?.countKey || "1",
                                    cur
                                ) || 0;
                        }
                        return acc;
                    }
                    return acc;
                };
                const afterReduce = (reduceResult) =>
                    reduceResult.count
                        ? +(reduceResult.mass / reduceResult.count).toFixed(
                              aggregateOptions?.fixed ?? 4
                          )
                        : 0;
                const global =
                    cache[tmpCacheKey] ||
                    filteredData.reduce(handler, cloneDeep(startingValue));
                cache[tmpCacheKey] = global;

                if (onlyGlobal) return formatValue(afterReduce(global));

                const local = pageData.reduce(
                    handler,
                    cloneDeep(startingValue)
                );
                if (asNumber) return afterReduce(local);
                return `~${formatValue(afterReduce(local))} | ~${formatValue(afterReduce(global))}`;
            }
            case AggrTypes.AVERAGE: {
                const startingValue = {mass: 0, count: 0};
                const handler = (acc, cur) => {
                    let tmp = cur[aggregateOptions?.massKey || field];
                    if (!isNil(tmp)) {
                        acc.mass += tmp;
                        acc.count++;
                        return acc;
                    }
                    return acc;
                };
                const afterReduce = (reduceResult) =>
                    reduceResult.count
                        ? +(reduceResult.mass / reduceResult.count).toFixed(
                              aggregateOptions?.fixed ?? 4
                          )
                        : 0;
                const global =
                    cache[tmpCacheKey] ||
                    filteredData.reduce(handler, cloneDeep(startingValue));
                cache[tmpCacheKey] = global;
                if (onlyGlobal) {
                    if (formatter) return `~${formatter(afterReduce(global))}`;
                    return `~${afterReduce(global)}`;
                }
                const local = pageData.reduce(
                    handler,
                    cloneDeep(startingValue)
                );
                if (formatter) {
                    return `~${formatter(afterReduce(local))} | ~${formatter(
                        afterReduce(global)
                    )}`;
                }
                return `~${afterReduce(local)} | ~${afterReduce(global)}`;
            }
            case AggrTypes.COUNT_DISTINCT: {
                const global =
                    cache[tmpCacheKey] || getCountDistinct(filteredData, field);
                cache[tmpCacheKey] = global;

                if (onlyGlobal) return formatValue(global);

                const local = getCountDistinct(pageData, field);

                if (asNumber) return local;
                return `${formatValue(local)} | ${formatValue(global)}`;
            }
            case AggrTypes.MOST_COMMON: {
                const global =
                    cache[tmpCacheKey] ||
                    getMostCommon(
                        filteredData,
                        field,
                        aggregateOptions?.countKey
                    );
                cache[tmpCacheKey] = global;
                if (onlyGlobal) {
                    if (
                        global === null ||
                        global === undefined ||
                        global === "null" // getMostCommon zwraca wartosci jako string
                    )
                        return "";
                    if (formatter) return formatter(global);
                    return global;
                }
                const local = getMostCommon(
                    pageData,
                    field,
                    aggregateOptions?.countKey
                );
                if (asNumber) return formatter ? formatter(local) : local;
                if (formatter) {
                    return `${formatter(local)} | ${formatter(global)}`;
                }
                return `${local} | ${global}`;
            }
            case AggrTypes.MIN: {
                const global =
                    cache[tmpCacheKey] || minBy(filteredData, field)?.[field];
                cache[tmpCacheKey] = global;
                if (onlyGlobal) return formatValue(global);

                const local = minBy(pageData, field)?.[field];

                if (asNumber) return local;
                return `${formatValue(local)} | ${formatValue(global)}`;
            }
            case AggrTypes.MAX: {
                const global =
                    cache[tmpCacheKey] || maxBy(filteredData, field)?.[field];
                cache[tmpCacheKey] = global;
                if (onlyGlobal) return formatValue(global);

                const local = maxBy(pageData, field)?.[field];

                if (asNumber) return local;
                return `${formatValue(local)} | ${formatValue(global)}`;
            }
            default: {
                const handler = (acc, cur) => acc + (cur[field] ?? 0);
                const startingValue = 0;
                const global =
                    cache[tmpCacheKey] ||
                    filteredData.reduce(handler, startingValue);
                cache[tmpCacheKey] = global;

                if (onlyGlobal) return formatValue(global);

                const local = pageData.reduce(handler, startingValue);

                if (asNumber)
                    return +local.toFixed(aggregateOptions?.fixed ?? 1);
                return `${+local.toFixed(
                    aggregateOptions?.fixed ?? 1
                )} | ${+global.toFixed(aggregateOptions?.fixed ?? 1)}`;
            }
        }
    } catch (e) {
        console.error(e);
        return `? | ?`;
    }
}
